/* eslint-disable object-property-newline */
/* eslint-disable import/no-cycle  */
import { MessageTypes, MessageSubtypes } from './constants';
import { Message, FileMessage, FileMessageInput, TextMessage, MessageParamsInput, PreviewMessage } from './models';
import calculateGroupParams from './utils/calculateGroupParams'

interface ParseResult {
  isAlreadyExists: boolean;
  isShouldReplaceExisting: boolean;
  index: number;
}

export const regLink = /(?:(?:https?|ftp|ftps|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gi;

export const createMessage = (messageParamsInput: MessageParamsInput): Message => {
  const { subtype } = messageParamsInput

  if (subtype === MessageSubtypes.PREVIEW) {
    return new PreviewMessage(messageParamsInput);
  }

  if (subtype === MessageSubtypes.FILE || subtype === MessageSubtypes.FILE_UPLOAD) {
    return new FileMessage(messageParamsInput as FileMessageInput);
  }

  return new TextMessage(messageParamsInput);
};

export const hasLinkPreview = (messages: Message[], parentClientTimestamp: string | number, link: string): boolean => (
  messages.some(message => (
    message.subtype === MessageSubtypes.PREVIEW
    && message.parentClientTimestamp === String(parentClientTimestamp)
    && 'url' in message
    && message.url === link
  ))
);

export const addMessage = (
  { messages, roomId, newMessage }: {messages: Message[], roomId: string, newMessage: Message}
): Message[] => {
  const { isAlreadyExists, isShouldReplaceExisting, index } = parseMessagesRegardingNewMessage(newMessage, messages);

  if (process.env.APP_TYPE === 'operator') { /* для дебага сортироваки в РОП на проде, удалить когда пофиксим */
    const hashText = (text: string, length = 6) => {
      let hash = 2166136261;
      for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
      }
      return (hash >>> 0).toString(36).slice(0, length);
    }
    console.warn(
      `${hashText(newMessage.text)}${newMessage.text ? newMessage.text[0] : ' '} `,
      'isExists=', isAlreadyExists,
      'replace=', isShouldReplaceExisting,
      'ii=', `${index}    `.slice(0, 4),
      'ct=', `${newMessage.clientTimestamp}               `.slice(0, 13),
      'd=', newMessage.date,
      'rid=', roomId,
    )
  }

  if (isAlreadyExists) return messages

  let newMessages = messages.slice();
  newMessages.splice(index, +isShouldReplaceExisting, newMessage)
  newMessages = calculateGroupParams(newMessages, index)
  return newMessages
}

export const deleteMessage = ({ messages, messageId }: {messages: Message[], messageId: Message['id']}): Message[] => {
  let newMessages = messages.slice();

  for (let ii = newMessages.length; ii--;) {
    if ((newMessages[ii]).id === messageId) {
      newMessages.splice(ii, 1);
      newMessages = calculateGroupParams(newMessages, ii - 1)
      return newMessages
    }
  }
  return newMessages
}

export const updateMessage = (
  { messages, messageId, modCount, lastEditedTime, originalTextHtml, translatedTextHtml }: {
    messages: Message[],
    messageId: Message['id'],
    modCount: Message['modCount'],
    lastEditedTime: Message['lastEditedTime'],
    originalTextHtml: string;
    translatedTextHtml: string;
  }
): Message[] => {
  let newMessages = messages.slice();
  for (let ii = newMessages.length; ii--;) {
    if (newMessages[ii].id === messageId) {
      newMessages[ii] = createMessage({
        ...newMessages[ii], modCount, lastEditedTime, text: originalTextHtml, translatedTextHtml,
      })
      newMessages = calculateGroupParams(newMessages, ii)
      return newMessages
    }
  }
  return newMessages
}

export const getLatestTalkerMessage = ({ messages, nickname }: {messages: Message[], nickname: Message['nickname']}) => {
  for (let ii = messages.length; ii--;) {
    if (
      messages[ii].nickname === nickname
      && (messages[ii].subtype === MessageSubtypes.MESSAGE || messages[ii].subtype === MessageSubtypes.LINK)
    ) {
      return messages[ii]
    }
  }
}

export const numberMessageFromEndAmongVisitorMessages = ({ messages, id, nickname }: {messages: Message[], id: Message['id'], nickname: Message['nickname']}) => {
  for (let ii = messages.length, number = 0; ii--;) {
    if (
      messages[ii].nickname === nickname
      && (messages[ii].subtype === MessageSubtypes.MESSAGE || messages[ii].subtype === MessageSubtypes.LINK)
    ) {
      number++
    }
    if (messages[ii].id === id) {
      return number
    }
  }
}

export function parseMessagesRegardingNewMessage(newMessage: Message, messages: Message[]): ParseResult {
  let res = {
    isAlreadyExists: false,
    isShouldReplaceExisting: false,
    index: messages.length,
  };

  /* for link-preview messages should prevent default */
  if (newMessage.subtype === MessageSubtypes.PREVIEW) {
    const parentMessage = messages.find(msg => msg.clientTimestamp === newMessage.parentClientTimestamp);
    const indexOfParentMessage = parentMessage ? messages.indexOf(parentMessage) : -1;
    res.index = indexOfParentMessage + 1;
    // console.warn('link-preview ')
    return res
  }

  /* fake message always goes to the end of the list */
  if (newMessage.isFake) {
    // console.warn('isFake ')
    return res
  }

  /* main parse by date (message.created from server) */
  for (let ii = messages.length; ii--;) {
    const message = messages[ii];
    if (newMessage.date! > message.date!) break

    res.index = ii;
    if (newMessage.date === message.date && newMessage.nickname === message.nickname) {
      res.isAlreadyExists = true;
      // console.warn('message.created ')
      return res
    }
  }

  const isSystemMessage = newMessage.type === MessageTypes.SYSTEM || newMessage.clientTimestamp === '0'
  //  if (isSystemMessage)  console.warn('isSystemMessage ')
  if (isSystemMessage) return res

  /* try replacing the message when updating a nickname */
  for (let ii = messages.length; ii--;) {
    const message = messages[ii]
    if (
      newMessage.nickname !== message.nickname
      && newMessage.clientTimestamp === message.clientTimestamp
      && newMessage.text === message.text
    ) {
      res.index = ii
      res.isShouldReplaceExisting = true
      return res
    }
  }

  /* secondary parse by client timestamp, between messages from the same user nearby */
  for (let ii = messages.length; ii--;) {
    const message = messages[ii];
    if (newMessage.nickname !== message.nickname) continue
    // if (message.clientTimestamp === '0') continue /* is message from start chat form */

    // if (newMessage.clientTimestamp > message.clientTimestamp) console.warn('newMessage.clientTimestamp > message.clientTimestamp ')
    if (newMessage.clientTimestamp! > message.clientTimestamp!) return res

    res.index = ii;
    if (newMessage.clientTimestamp === message.clientTimestamp) {      
      if (message.isFake || newMessage.text !== message.text) {
        res.isShouldReplaceExisting = true;
      } else {
        res.isAlreadyExists = true;
      }
      // console.warn('pre fin')
      return res
    }
  }

  // console.warn('fin ')
  return res
}

export function tryChangeVisitorNicknameToNicknameWithNumber(
  messages: Message[],
  currentNickName: Message['nickname'],
  newNickName: Message['nickname']
): Message[] {
  if (currentNickName === newNickName) return messages
  return messages.map(message => {
    if (message.nickname !== currentNickName) return message
    message.nickname = newNickName
    return message
  });
}
